import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/construction';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE', accentDark:'#2E8B47',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
  purple:'#6B48A8', purpleBg:'#F0EBFB', purpleBorder:'#C8B8F0',
  teal:'#1A7A7A', tealBg:'#E7F5F5', tealBorder:'#A8D8D8',
};

const inr = n => 'Rs.' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });
const today = () => new Date().toISOString().slice(0,10);

const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,.05)', ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', style={}, disabled }) => {
  const v = {
    primary:{ background:C.accent, color:'#fff', border:'none' },
    danger: { background:C.red, color:'#fff', border:'none' },
    ghost:  { background:'transparent', color:C.text2, border:`1px solid ${C.border2}` },
    light:  { background:C.accentLight, color:C.accent, border:'none' },
    amber:  { background:C.amberBg, color:C.amber, border:`1px solid ${C.amberBorder}` },
  };
  return <button onClick={onClick} disabled={disabled}
    style={{ ...v[variant], padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
      cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
      display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap', ...style }}>{children}</button>;
};

const Inp = ({ label, value, onChange, type='text', opts, placeholder='' }) => {
  const base = { width:'100%', padding:'9px 12px', background:C.bg, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box' };
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5 }}>{label}</div>}
      {opts
        ? <select style={base} value={value} onChange={e=>onChange(e.target.value)}>
            {opts.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
          </select>
        : <input type={type} style={base} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
};

const Modal = ({ title, onClose, children, width='520px' }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    <Card style={{ width, maxWidth:'100%', maxHeight:'90vh', overflow:'auto', padding:28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{title}</div>
        <button onClick={onClose} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, width:32, height:32, cursor:'pointer', color:C.text2 }}>X</button>
      </div>
      {children}
    </Card>
  </div>
);

const Th = ({ c }) => <th style={{ padding:'10px 14px', fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', textAlign:'left', background:'#F5F8F5', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{c}</th>;
const Td = ({ children, style={} }) => <td style={{ padding:'11px 14px', fontSize:13, borderBottom:`1px solid ${C.border}`, verticalAlign:'middle', ...style }}>{children}</td>;
const Empty = ({ msg }) => <div style={{ padding:'40px 20px', textAlign:'center', color:C.text3, fontSize:14 }}>{msg}</div>;

const statusColor = s => {
  const map = {
    in_progress:[C.accent,C.accentLight,'#A8D8B8'], planning:[C.blue,C.blueBg,C.blueBorder],
    completed:[C.teal,C.tealBg,C.tealBorder], on_hold:[C.amber,C.amberBg,C.amberBorder],
    pending:[C.amber,C.amberBg,C.amberBorder], paid:[C.accent,C.accentLight,'#A8D8B8'],
    partial:[C.blue,C.blueBg,C.blueBorder], open:[C.red,C.redBg,C.redBorder],
    resolved:[C.accent,C.accentLight,'#A8D8B8'], high:[C.red,C.redBg,C.redBorder],
    medium:[C.amber,C.amberBg,C.amberBorder], low:[C.blue,C.blueBg,C.blueBorder],
  };
  return map[s] || [C.text3,C.border,C.border2];
};

const Badge = ({ label }) => {
  const [col,bg,brd] = statusColor(label);
  return <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 10px', borderRadius:99, fontSize:11, fontWeight:600, color:col, background:bg, border:`1px solid ${brd}` }}>
    <span style={{ width:5, height:5, borderRadius:'50%', background:col }} />{label?.replace('_',' ')}
  </span>;
};

const ProgressBar = ({ pct, color=C.accent }) => (
  <div>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.text3, marginBottom:4 }}>
      <span>Progress</span><span style={{ fontWeight:700, color }}>{pct}%</span>
    </div>
    <div style={{ height:6, background:C.border, borderRadius:99 }}>
      <div style={{ height:6, width:`${Math.min(pct,100)}%`, background:color, borderRadius:99, transition:'width .4s' }} />
    </div>
  </div>
);

const Stat = ({ label, value, sub, color=C.accent, bg=C.accentLight, onClick }) => (
  <Card onClick={onClick} style={{ padding:18, cursor:onClick?'pointer':'default' }}>
    <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, letterSpacing:'-.02em' }}>{value}</div>
    {sub && <div style={{ fontSize:12, color:C.text3, marginTop:3 }}>{sub}</div>}
  </Card>
);

// ── DASHBOARD ──────────────────────────────────────────────────────────────
function Dashboard({ projects, setTab }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/dashboard`).then(r=>r.json()).then(setData).catch(()=>{});
  }, []);

  const inProgress = (data?.projects||[]).find(p=>p.status==='in_progress');
  const totalBudget = (data?.projects||[]).reduce((s,p)=>s+parseFloat(p.budget||0),0);
  const totalSpent = (data?.projects||[]).reduce((s,p)=>s+parseFloat(p.spent||0),0);
  const pendingBills = (data?.bills||[]).find(b=>b.status==='pending');
  const openSnags = (data?.snags||[]).reduce((s,g)=>s+parseInt(g.count),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:22 }}>
        <Stat label="Active Projects" value={inProgress?.count||0} sub="In progress" onClick={()=>setTab('projects')} />
        <Stat label="Total Budget" value={inr(totalBudget)} sub="All projects" color={C.blue} bg={C.blueBg} onClick={()=>setTab('projects')} />
        <Stat label="Spent to Date" value={inr(totalSpent)} sub="All projects" color={C.purple} bg={C.purpleBg} onClick={()=>setTab('bills')} />
        <Stat label="Workers Today" value={data?.workers_today||0} sub="On site" color={C.teal} bg={C.tealBg} onClick={()=>setTab('labour')} />
        <Stat label="Pending Bills" value={pendingBills?.count||0} sub="Awaiting approval" color={C.amber} bg={C.amberBg} onClick={()=>setTab('bills')} />
        <Stat label="Open Snags" value={openSnags} sub="Quality issues" color={C.red} bg={C.redBg} onClick={()=>setTab('snags')} />
      </div>

      {/* Project Cards */}
      <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:14 }}>Active Projects</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14, marginBottom:20 }}>
        {projects.filter(p=>p.status!=='completed').map(p => {
          const budget = parseFloat(p.total_budget||0);
          const spent = parseFloat(p.spent_to_date||0);
          const overrun = spent > budget;
          return (
            <Card key={p.id} style={{ padding:20, borderTop:`4px solid ${p.priority==='high'?C.red:p.priority==='medium'?C.amber:C.accent}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:11, fontFamily:'monospace', color:C.text3, marginBottom:4 }}>{p.project_code}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{p.name}</div>
                  <div style={{ fontSize:12, color:C.text3, marginTop:2 }}>{p.location}</div>
                </div>
                <Badge label={p.status} />
              </div>
              <div style={{ marginBottom:14 }}>
                <ProgressBar pct={p.progress_pct||0} color={p.progress_pct>=80?C.accent:p.progress_pct>=40?C.blue:C.amber} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>Budget</div>
                  <div style={{ fontSize:13, fontWeight:700 }}>{inr(budget)}</div>
                </div>
                <div style={{ background:overrun?C.redBg:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>Spent</div>
                  <div style={{ fontSize:13, fontWeight:700, color:overrun?C.red:C.text }}>{inr(spent)}</div>
                </div>
                <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>Due Date</div>
                  <div style={{ fontSize:12, fontWeight:600, color: p.expected_end_date < today() ? C.red : C.text }}>{p.expected_end_date?.slice(0,10)}</div>
                </div>
                <div style={{ background:C.bg, borderRadius:7, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:C.text3 }}>Manager</div>
                  <div style={{ fontSize:12, fontWeight:600 }}>{p.project_manager||'N/A'}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* AI Insights */}
      <Card style={{ padding:20, background:'linear-gradient(135deg,#0A1A0F,#0F2A14)', border:'none' }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:10, textTransform:'uppercase', letterSpacing:'.08em' }}>AI Construction Insights</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
          {[
            ['Budget Alert', 'Monitor spending on New Cottages Block A - approaching 50% budget at 45% completion.'],
            ['Timeline Risk', 'Staff Quarters Upgrade at 85% - ensure punch list completion before deadline.'],
            ['Material Prediction', 'Cement consumption rate suggests reorder needed in 12 days at current usage.'],
            ['Pilferage Control', 'Log material returns daily to detect wastage above 15% threshold automatically.'],
          ].map(([title, text]) => (
            <div key={title} style={{ padding:'12px 14px', background:'rgba(58,175,88,.08)', borderRadius:10, border:'1px solid rgba(58,175,88,.2)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:5 }}>{title}</div>
              <div style={{ fontSize:12, color:'rgba(240,248,242,.7)', lineHeight:1.5 }}>{text}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── PROJECTS ───────────────────────────────────────────────────────────────
function ProjectsTab({ projects, reload }) {
  const [showModal, setShowModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(null);
  const [form, setForm] = useState({ project_code:'', name:'', category:'civil', location:'', description:'', priority:'medium', start_date:today(), expected_end_date:'', total_budget:'', project_manager:'', site_engineer:'' });
  const [updateForm, setUpdateForm] = useState({ status:'', progress_pct:'', spent_to_date:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const uf = k => v => setUpdateForm(p=>({...p,[k]:v}));

  const save = async () => {
    const code = form.project_code || `BTX-C${String(Date.now()).slice(-4)}`;
    await fetch(`${API}/projects`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...form, project_code:code}) });
    setShowModal(false); reload();
  };

  const update = async () => {
    await fetch(`${API}/projects/${updateModal.id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(updateForm) });
    setUpdateModal(null); reload();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>All Projects</div>
        <Btn onClick={()=>setShowModal(true)}>+ New Project</Btn>
      </div>
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Code" /><Th c="Project" /><Th c="Category" /><Th c="Progress" /><Th c="Budget" /><Th c="Spent" /><Th c="End Date" /><Th c="Status" /><Th c="Action" /></tr></thead>
            <tbody>
              {projects.length===0 ? <tr><td colSpan={9}><Empty msg="No projects yet" /></td></tr>
                : projects.map(p => {
                  const overrun = parseFloat(p.spent_to_date||0) > parseFloat(p.total_budget||0);
                  return (
                    <tr key={p.id}>
                      <Td><span style={{ fontFamily:'monospace', fontSize:11, background:C.bg, padding:'2px 7px', borderRadius:5, color:C.text3 }}>{p.project_code}</span></Td>
                      <Td><div style={{ fontWeight:700 }}>{p.name}</div><div style={{ fontSize:11, color:C.text3 }}>{p.location}</div></Td>
                      <Td style={{ color:C.text3 }}>{p.category}</Td>
                      <Td style={{ minWidth:120 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:5, background:C.border, borderRadius:99 }}>
                            <div style={{ height:5, width:`${p.progress_pct||0}%`, background:C.accent, borderRadius:99 }} />
                          </div>
                          <span style={{ fontSize:12, fontWeight:700, color:C.accent, minWidth:30 }}>{p.progress_pct||0}%</span>
                        </div>
                      </Td>
                      <Td><span style={{ fontWeight:600 }}>{inr(p.total_budget)}</span></Td>
                      <Td><span style={{ fontWeight:600, color:overrun?C.red:C.text }}>{inr(p.spent_to_date)}</span></Td>
                      <Td style={{ fontSize:12, color: p.expected_end_date < today() && p.status!=='completed' ? C.red : C.text3 }}>{p.expected_end_date?.slice(0,10)}</Td>
                      <Td><Badge label={p.status} /></Td>
                      <Td><Btn variant="light" onClick={()=>{ setUpdateForm({status:p.status,progress_pct:p.progress_pct||0,spent_to_date:p.spent_to_date||0}); setUpdateModal(p); }} style={{ padding:'4px 12px', fontSize:12 }}>Update</Btn></Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="New Project" onClose={()=>setShowModal(false)} width="580px">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Project Code" value={form.project_code} onChange={f('project_code')} placeholder="Auto-generated if blank" />
            <Inp label="Category" value={form.category} onChange={f('category')} opts={['civil','electrical','plumbing','interior','landscaping','mechanical','other']} />
          </div>
          <Inp label="Project Name" value={form.name} onChange={f('name')} placeholder="e.g. New Cottages Block B" />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Location" value={form.location} onChange={f('location')} placeholder="e.g. North Wing" />
            <Inp label="Priority" value={form.priority} onChange={f('priority')} opts={['low','medium','high']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Start Date" value={form.start_date} onChange={f('start_date')} type="date" />
            <Inp label="Expected End Date" value={form.expected_end_date} onChange={f('expected_end_date')} type="date" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Total Budget (Rs)" value={form.total_budget} onChange={f('total_budget')} type="number" />
            <Inp label="Project Manager" value={form.project_manager} onChange={f('project_manager')} placeholder="Name" />
          </div>
          <Inp label="Description" value={form.description} onChange={f('description')} placeholder="Brief description..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Create Project</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {updateModal && (
        <Modal title={`Update - ${updateModal.name}`} onClose={()=>setUpdateModal(null)} width="400px">
          <Inp label="Status" value={updateForm.status} onChange={uf('status')} opts={['planning','in_progress','on_hold','completed']} />
          <Inp label="Progress %" value={updateForm.progress_pct} onChange={uf('progress_pct')} type="number" placeholder="0-100" />
          <Inp label="Total Spent to Date (Rs)" value={updateForm.spent_to_date} onChange={uf('spent_to_date')} type="number" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={update} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setUpdateModal(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── MILESTONES ─────────────────────────────────────────────────────────────
function MilestonesTab({ projects }) {
  const [milestones, setMilestones] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(null);
  const [form, setForm] = useState({ project_id:'', title:'', description:'', planned_date:'', notes:'' });
  const [updateForm, setUpdateForm] = useState({ status:'', progress_pct:'', actual_date:'', notes:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const uf = k => v => setUpdateForm(p=>({...p,[k]:v}));

  const load = useCallback(() => {
    const q = filterProject ? `?project_id=${filterProject}` : '';
    fetch(`${API}/milestones${q}`).then(r=>r.json()).then(d=>setMilestones(Array.isArray(d)?d:[])).catch(()=>{});
  }, [filterProject]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await fetch(`${API}/milestones`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const update = async () => {
    await fetch(`${API}/milestones/${updateModal.id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(updateForm) });
    setUpdateModal(null); load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Milestones & Timeline</div>
        <div style={{ display:'flex', gap:10 }}>
          <select value={filterProject} onChange={e=>setFilterProject(e.target.value)}
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
            <option value="">All Projects</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Btn onClick={()=>setShowModal(true)}>+ Add Milestone</Btn>
        </div>
      </div>
      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Project" /><Th c="Milestone" /><Th c="Planned Date" /><Th c="Progress" /><Th c="Status" /><Th c="Delay" /><Th c="Action" /></tr></thead>
            <tbody>
              {milestones.length===0 ? <tr><td colSpan={7}><Empty msg="No milestones yet" /></td></tr>
                : milestones.map(m => {
                  const isOverdue = m.planned_date < today() && m.status !== 'completed';
                  const delayDays = isOverdue ? Math.floor((new Date()-new Date(m.planned_date))/86400000) : 0;
                  return (
                    <tr key={m.id} style={{ background:isOverdue?'#FFF5F5':'transparent' }}>
                      <Td style={{ color:C.text3, fontSize:12 }}>{m.project_name}</Td>
                      <Td><div style={{ fontWeight:600 }}>{m.title}</div>{m.description&&<div style={{ fontSize:11, color:C.text3 }}>{m.description.slice(0,50)}</div>}</Td>
                      <Td style={{ fontSize:12, color:isOverdue?C.red:C.text3, fontWeight:isOverdue?700:400 }}>{m.planned_date?.slice(0,10)}</Td>
                      <Td style={{ minWidth:120 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ flex:1, height:5, background:C.border, borderRadius:99 }}>
                            <div style={{ height:5, width:`${m.progress_pct||0}%`, background:C.accent, borderRadius:99 }} />
                          </div>
                          <span style={{ fontSize:12, fontWeight:700, color:C.accent }}>{m.progress_pct||0}%</span>
                        </div>
                      </Td>
                      <Td><Badge label={m.status} /></Td>
                      <Td style={{ color:delayDays>0?C.red:C.text3, fontWeight:delayDays>0?700:400 }}>{delayDays>0?`${delayDays} days`:'-'}</Td>
                      <Td><Btn variant="light" onClick={()=>{ setUpdateForm({status:m.status,progress_pct:m.progress_pct||0,actual_date:m.actual_date?.slice(0,10)||'',notes:m.notes||''}); setUpdateModal(m); }} style={{ padding:'4px 12px', fontSize:12 }}>Update</Btn></Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="Add Milestone" onClose={()=>setShowModal(false)}>
          <Inp label="Project" value={form.project_id} onChange={f('project_id')} opts={[{value:'',label:'Select project'},...projects.map(p=>({value:p.id,label:p.name}))]} />
          <Inp label="Milestone Title" value={form.title} onChange={f('title')} placeholder="e.g. Foundation Complete" />
          <Inp label="Planned Date" value={form.planned_date} onChange={f('planned_date')} type="date" />
          <Inp label="Description" value={form.description} onChange={f('description')} placeholder="Details..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {updateModal && (
        <Modal title={`Update - ${updateModal.title}`} onClose={()=>setUpdateModal(null)} width="400px">
          <Inp label="Status" value={updateForm.status} onChange={uf('status')} opts={['pending','in_progress','completed','delayed']} />
          <Inp label="Progress %" value={updateForm.progress_pct} onChange={uf('progress_pct')} type="number" />
          <Inp label="Actual Completion Date" value={updateForm.actual_date} onChange={uf('actual_date')} type="date" />
          <Inp label="Notes" value={updateForm.notes} onChange={uf('notes')} placeholder="Update notes..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={update} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setUpdateModal(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── MATERIALS ──────────────────────────────────────────────────────────────
function MaterialsTab({ projects }) {
  const [materials, setMaterials] = useState([]);
  const [usage, setUsage] = useState([]);
  const [pilferage, setPilferage] = useState([]);
  const [subTab, setSubTab] = useState('stock');
  const [showMaterial, setShowMaterial] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [matForm, setMatForm] = useState({ name:'', category:'binding', unit:'bags', current_stock:'', reorder_level:'', rate_per_unit:'', supplier:'' });
  const [usageForm, setUsageForm] = useState({ project_id:'', material_id:'', usage_date:today(), quantity_issued:'', quantity_predicted:'', quantity_returned:'0', issued_by:'', received_by:'', notes:'' });
  const mf = k => v => setMatForm(p=>({...p,[k]:v}));
  const uf = k => v => setUsageForm(p=>({...p,[k]:v}));

  const load = () => {
    fetch(`${API}/materials`).then(r=>r.json()).then(d=>setMaterials(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/usage`).then(r=>r.json()).then(d=>setUsage(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/pilferage`).then(r=>r.json()).then(d=>setPilferage(Array.isArray(d)?d:[])).catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  const saveMat = async () => {
    await fetch(`${API}/materials`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(matForm) });
    setShowMaterial(false); load();
  };

  const saveUsage = async () => {
    await fetch(`${API}/usage`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(usageForm) });
    setShowUsage(false); load();
  };

  const lowStock = materials.filter(m=>parseFloat(m.current_stock)<=parseFloat(m.reorder_level));

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Materials & Pilferage Control</div>
        <div style={{ display:'flex', gap:10 }}>
          <Btn variant="ghost" onClick={()=>setShowMaterial(true)}>+ Add Material</Btn>
          <Btn onClick={()=>setShowUsage(true)}>+ Issue Material</Btn>
        </div>
      </div>

      {pilferage.length > 0 && (
        <div style={{ background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:C.red, fontWeight:600 }}>
          PILFERAGE ALERT: {pilferage.length} material issuance(s) show wastage above 15% threshold — review immediately!
        </div>
      )}

      {lowStock.length > 0 && (
        <div style={{ background:C.amberBg, border:`1px solid ${C.amberBorder}`, borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:C.amber, fontWeight:500 }}>
          Low Stock: {lowStock.map(m=>m.name).join(', ')}
        </div>
      )}

      <div style={{ display:'flex', gap:8, marginBottom:14 }}>
        {[['stock','Stock Register'],['usage','Usage Log'],['pilferage','Pilferage Alerts']].map(([id,label]) => (
          <button key={id} onClick={()=>setSubTab(id)}
            style={{ padding:'7px 16px', borderRadius:8, border:`1px solid ${subTab===id?C.accent:C.border2}`,
              background:subTab===id?C.accentLight:'transparent', color:subTab===id?C.accent:C.text3,
              fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {label}{id==='pilferage'&&pilferage.length>0?` (${pilferage.length})`:''}
          </button>
        ))}
      </div>

      {subTab==='stock' && (
        <Card>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr><Th c="Material" /><Th c="Category" /><Th c="Stock" /><Th c="Reorder At" /><Th c="Rate" /><Th c="Value" /><Th c="Supplier" /><Th c="Status" /></tr></thead>
              <tbody>
                {materials.map(m => {
                  const isLow = parseFloat(m.current_stock)<=parseFloat(m.reorder_level);
                  const value = parseFloat(m.current_stock||0)*parseFloat(m.rate_per_unit||0);
                  return (
                    <tr key={m.id} style={{ background:isLow?'#FFF5F5':'transparent' }}>
                      <Td><span style={{ fontWeight:600 }}>{m.name}</span></Td>
                      <Td style={{ color:C.text3 }}>{m.category}</Td>
                      <Td><span style={{ fontWeight:700, color:isLow?C.red:C.accent }}>{m.current_stock} {m.unit}</span></Td>
                      <Td style={{ color:C.text3 }}>{m.reorder_level} {m.unit}</Td>
                      <Td style={{ color:C.text3 }}>{inr(m.rate_per_unit)}</Td>
                      <Td><span style={{ fontWeight:600 }}>{inr(value)}</span></Td>
                      <Td style={{ color:C.text3 }}>{m.supplier||'-'}</Td>
                      <Td>{isLow ? <Badge label="Reorder" /> : <Badge label="in_progress" />}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {subTab==='usage' && (
        <Card>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr><Th c="Date" /><Th c="Project" /><Th c="Material" /><Th c="Issued" /><Th c="Predicted" /><Th c="Returned" /><Th c="Net Used" /><Th c="Wastage" /><Th c="Flag" /></tr></thead>
              <tbody>
                {usage.length===0 ? <tr><td colSpan={9}><Empty msg="No usage logged yet" /></td></tr>
                  : usage.map(u => (
                    <tr key={u.id} style={{ background:u.pilferage_flag?'#FFF5F5':'transparent' }}>
                      <Td style={{ fontSize:12, color:C.text3 }}>{u.usage_date?.slice(0,10)}</Td>
                      <Td style={{ color:C.text3 }}>{u.project_name}</Td>
                      <Td><span style={{ fontWeight:600 }}>{u.material_name}</span></Td>
                      <Td><span style={{ fontWeight:600 }}>{u.quantity_issued} {u.unit}</span></Td>
                      <Td style={{ color:C.text3 }}>{u.quantity_predicted} {u.unit}</Td>
                      <Td style={{ color:C.text3 }}>{u.quantity_returned} {u.unit}</Td>
                      <Td><span style={{ fontWeight:700, color:C.accent }}>{u.net_used} {u.unit}</span></Td>
                      <Td style={{ color: parseFloat(u.wastage_pct)>15?C.red:C.text3, fontWeight:parseFloat(u.wastage_pct)>15?700:400 }}>
                        {parseFloat(u.wastage_pct||0).toFixed(1)}%
                      </Td>
                      <Td>{u.pilferage_flag && <Badge label="high" />}</Td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {subTab==='pilferage' && (
        pilferage.length===0
          ? <Card style={{ padding:40, textAlign:'center', color:C.accent, fontWeight:600 }}>No pilferage alerts - all material usage within acceptable limits!</Card>
          : <Card>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><Th c="Date" /><Th c="Project" /><Th c="Material" /><Th c="Issued" /><Th c="Predicted" /><Th c="Net Used" /><Th c="Wastage %" /><Th c="Issued By" /></tr></thead>
                <tbody>
                  {pilferage.map(u => (
                    <tr key={u.id} style={{ background:'#FFF5F5' }}>
                      <Td style={{ fontSize:12, color:C.red, fontWeight:700 }}>{u.usage_date?.slice(0,10)}</Td>
                      <Td style={{ color:C.text3 }}>{u.project_name}</Td>
                      <Td><span style={{ fontWeight:700, color:C.red }}>{u.material_name}</span></Td>
                      <Td>{u.quantity_issued} {u.unit}</Td>
                      <Td style={{ color:C.text3 }}>{u.quantity_predicted} {u.unit}</Td>
                      <Td><span style={{ fontWeight:700 }}>{u.net_used} {u.unit}</span></Td>
                      <Td><span style={{ fontWeight:800, color:C.red, fontSize:14 }}>{parseFloat(u.wastage_pct||0).toFixed(1)}%</span></Td>
                      <Td style={{ color:C.text3 }}>{u.issued_by||'-'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
      )}

      {showMaterial && (
        <Modal title="Add Material" onClose={()=>setShowMaterial(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Material Name" value={matForm.name} onChange={mf('name')} placeholder="e.g. Cement OPC 53" />
            <Inp label="Category" value={matForm.category} onChange={mf('category')} opts={['binding','steel','aggregate','masonry','shuttering','finishing','plumbing','electrical','other']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Current Stock" value={matForm.current_stock} onChange={mf('current_stock')} type="number" />
            <Inp label="Unit" value={matForm.unit} onChange={mf('unit')} opts={['bags','kg','tonnes','cft','sft','rft','nos','ltrs','mtr','sets']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Reorder Level" value={matForm.reorder_level} onChange={mf('reorder_level')} type="number" />
            <Inp label="Rate per Unit (Rs)" value={matForm.rate_per_unit} onChange={mf('rate_per_unit')} type="number" />
          </div>
          <Inp label="Supplier" value={matForm.supplier} onChange={mf('supplier')} placeholder="Supplier name" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={saveMat} style={{ flex:1, justifyContent:'center' }}>Add</Btn>
            <Btn variant="ghost" onClick={()=>setShowMaterial(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {showUsage && (
        <Modal title="Issue Material" onClose={()=>setShowUsage(false)}>
          <div style={{ padding:'10px 14px', background:C.amberBg, borderRadius:8, marginBottom:14, fontSize:12, color:C.amber }}>
            System auto-flags wastage above 15% as potential pilferage. Enter predicted quantity for accurate tracking.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Project" value={usageForm.project_id} onChange={uf('project_id')} opts={[{value:'',label:'Select project'},...projects.map(p=>({value:p.id,label:p.name}))]} />
            <Inp label="Material" value={usageForm.material_id} onChange={uf('material_id')} opts={[{value:'',label:'Select material'},...materials.map(m=>({value:m.id,label:`${m.name} (${m.current_stock} ${m.unit})`}))]} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity Issued" value={usageForm.quantity_issued} onChange={uf('quantity_issued')} type="number" placeholder="Actual qty given" />
            <Inp label="Quantity Predicted" value={usageForm.quantity_predicted} onChange={uf('quantity_predicted')} type="number" placeholder="What should be needed" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Quantity Returned" value={usageForm.quantity_returned} onChange={uf('quantity_returned')} type="number" placeholder="0" />
            <Inp label="Date" value={usageForm.usage_date} onChange={uf('usage_date')} type="date" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Issued By" value={usageForm.issued_by} onChange={uf('issued_by')} placeholder="Store keeper name" />
            <Inp label="Received By" value={usageForm.received_by} onChange={uf('received_by')} placeholder="Contractor/worker" />
          </div>
          <Inp label="Notes" value={usageForm.notes} onChange={uf('notes')} placeholder="Work activity, location..." />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={saveUsage} style={{ flex:1, justifyContent:'center' }}>Issue Material</Btn>
            <Btn variant="ghost" onClick={()=>setShowUsage(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CONTRACTORS ────────────────────────────────────────────────────────────
function ContractorsTab() {
  const [contractors, setContractors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name:'', company:'', category:'civil', phone:'', email:'', gst_number:'', pan_number:'', address:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/contractors`).then(r=>r.json()).then(d=>setContractors(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/contractors`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Contractors</div>
        <Btn onClick={()=>setShowModal(true)}>+ Add Contractor</Btn>
      </div>
      {contractors.length===0 ? <Card style={{ padding:40, textAlign:'center', color:C.text3 }}>No contractors added yet</Card> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
          {contractors.map(c => (
            <Card key={c.id} style={{ padding:20 }}>
              <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:4 }}>{c.name}</div>
              {c.company && <div style={{ fontSize:13, color:C.text3, marginBottom:8 }}>{c.company}</div>}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[['Category',c.category],['Phone',c.phone||'-'],['GST',c.gst_number||'-'],['PAN',c.pan_number||'-']].map(([l,v]) => (
                  <div key={l} style={{ background:C.bg, borderRadius:7, padding:'7px 10px' }}>
                    <div style={{ fontSize:10, color:C.text3 }}>{l}</div>
                    <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{v}</div>
                  </div>
                ))}
              </div>
              {c.rating > 0 && (
                <div style={{ display:'flex', gap:3, marginTop:10 }}>
                  {[1,2,3,4,5].map(s => (
                    <div key={s} style={{ width:12, height:12, borderRadius:'50%', background:s<=c.rating?C.accent:C.border }} />
                  ))}
                  <span style={{ fontSize:11, color:C.text3, marginLeft:6 }}>{c.rating}/5</span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {showModal && (
        <Modal title="Add Contractor" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Name" value={form.name} onChange={f('name')} placeholder="Individual or firm name" />
            <Inp label="Company" value={form.company} onChange={f('company')} placeholder="Company name" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Category" value={form.category} onChange={f('category')} opts={['civil','electrical','plumbing','interior','masonry','painting','tiling','steel','other']} />
            <Inp label="Phone" value={form.phone} onChange={f('phone')} placeholder="Mobile number" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="GST Number" value={form.gst_number} onChange={f('gst_number')} placeholder="Optional" />
            <Inp label="PAN Number" value={form.pan_number} onChange={f('pan_number')} placeholder="Optional" />
          </div>
          <Inp label="Address" value={form.address} onChange={f('address')} placeholder="Full address" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── BILLS ──────────────────────────────────────────────────────────────────
function BillsTab({ projects }) {
  const [bills, setBills] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [form, setForm] = useState({ project_id:'', contractor_id:'', bill_number:'', bill_date:today(), work_description:'', bill_amount:'', gst_amount:'0', tds_deduction:'0', retention_money:'0', notes:'' });
  const [payForm, setPayForm] = useState({ paid_amount:'', payment_mode:'bank_transfer', approved_by:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));
  const pf = k => v => setPayForm(p=>({...p,[k]:v}));

  const load = useCallback(() => {
    const q = filterProject ? `?project_id=${filterProject}` : '';
    fetch(`${API}/bills${q}`).then(r=>r.json()).then(d=>setBills(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/contractors`).then(r=>r.json()).then(d=>setContractors(Array.isArray(d)?d:[])).catch(()=>{});
  }, [filterProject]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await fetch(`${API}/bills`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const pay = async () => {
    await fetch(`${API}/bills/${payModal.id}/pay`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payForm) });
    setPayModal(null); load();
  };

  const net = f => parseFloat(f.bill_amount||0) + parseFloat(f.gst_amount||0) - parseFloat(f.tds_deduction||0) - parseFloat(f.retention_money||0);
  const totalPending = bills.filter(b=>b.status!=='paid').reduce((s,b)=>s+parseFloat(b.net_payable||0)-parseFloat(b.paid_amount||0),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Contractor Bills</div>
        <div style={{ display:'flex', gap:10 }}>
          <select value={filterProject} onChange={e=>setFilterProject(e.target.value)}
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
            <option value="">All Projects</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Btn onClick={()=>setShowModal(true)}>+ Add Bill</Btn>
        </div>
      </div>

      {totalPending > 0 && (
        <div style={{ background:C.amberBg, border:`1px solid ${C.amberBorder}`, borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:C.amber, fontWeight:600 }}>
          Total Pending Payment: {inr(totalPending)}
        </div>
      )}

      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Bill No" /><Th c="Date" /><Th c="Project" /><Th c="Contractor" /><Th c="Bill Amt" /><Th c="GST" /><Th c="TDS" /><Th c="Net Payable" /><Th c="Paid" /><Th c="Status" /><Th c="Action" /></tr></thead>
            <tbody>
              {bills.length===0 ? <tr><td colSpan={11}><Empty msg="No bills added yet" /></td></tr>
                : bills.map(b => {
                  const balance = parseFloat(b.net_payable||0) - parseFloat(b.paid_amount||0);
                  return (
                    <tr key={b.id}>
                      <Td><span style={{ fontFamily:'monospace', fontSize:11, background:C.bg, padding:'2px 6px', borderRadius:5 }}>{b.bill_number||`BILL-${b.id}`}</span></Td>
                      <Td style={{ fontSize:12, color:C.text3 }}>{b.bill_date?.slice(0,10)}</Td>
                      <Td style={{ color:C.text3, fontSize:12 }}>{b.project_name}</Td>
                      <Td><span style={{ fontWeight:600 }}>{b.contractor_name||'Direct'}</span></Td>
                      <Td>{inr(b.bill_amount)}</Td>
                      <Td style={{ color:C.text3 }}>{inr(b.gst_amount)}</Td>
                      <Td style={{ color:C.red }}>{inr(b.tds_deduction)}</Td>
                      <Td><span style={{ fontWeight:700 }}>{inr(b.net_payable)}</span></Td>
                      <Td><span style={{ fontWeight:600, color:C.accent }}>{inr(b.paid_amount)}</span></Td>
                      <Td><Badge label={b.status} /></Td>
                      <Td>
                        {b.status !== 'paid' && (
                          <Btn variant="light" onClick={()=>{ setPayForm({paid_amount:balance,payment_mode:'bank_transfer',approved_by:''}); setPayModal(b); }}
                            style={{ padding:'4px 12px', fontSize:12 }}>Pay</Btn>
                        )}
                      </Td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="Add Contractor Bill" onClose={()=>setShowModal(false)} width="580px">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Project" value={form.project_id} onChange={f('project_id')} opts={[{value:'',label:'Select project'},...projects.map(p=>({value:p.id,label:p.name}))]} />
            <Inp label="Contractor" value={form.contractor_id} onChange={f('contractor_id')} opts={[{value:'',label:'Select or direct'},...contractors.map(c=>({value:c.id,label:c.name}))]} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Bill Number" value={form.bill_number} onChange={f('bill_number')} placeholder="e.g. INV-2026-001" />
            <Inp label="Bill Date" value={form.bill_date} onChange={f('bill_date')} type="date" />
          </div>
          <Inp label="Work Description" value={form.work_description} onChange={f('work_description')} placeholder="Description of work completed..." />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Bill Amount (Rs)" value={form.bill_amount} onChange={f('bill_amount')} type="number" />
            <Inp label="GST Amount (Rs)" value={form.gst_amount} onChange={f('gst_amount')} type="number" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="TDS Deduction (Rs)" value={form.tds_deduction} onChange={f('tds_deduction')} type="number" />
            <Inp label="Retention Money (Rs)" value={form.retention_money} onChange={f('retention_money')} type="number" />
          </div>
          <div style={{ padding:'12px 16px', background:C.accentLight, borderRadius:9, marginBottom:14, display:'flex', justifyContent:'space-between' }}>
            <span style={{ fontSize:13, color:C.text2 }}>Net Payable</span>
            <span style={{ fontSize:16, fontWeight:800, color:C.accent }}>{inr(net(form))}</span>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save Bill</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {payModal && (
        <Modal title={`Pay - ${payModal.contractor_name||'Contractor'}`} onClose={()=>setPayModal(null)} width="400px">
          <div style={{ padding:'10px 14px', background:C.bg, borderRadius:8, marginBottom:14 }}>
            <div style={{ fontSize:12, color:C.text3 }}>Net Payable: <strong>{inr(payModal.net_payable)}</strong></div>
            <div style={{ fontSize:12, color:C.text3 }}>Already Paid: <strong>{inr(payModal.paid_amount)}</strong></div>
            <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginTop:4 }}>Balance: {inr(parseFloat(payModal.net_payable)-parseFloat(payModal.paid_amount))}</div>
          </div>
          <Inp label="Payment Amount (Rs)" value={payForm.paid_amount} onChange={pf('paid_amount')} type="number" />
          <Inp label="Payment Mode" value={payForm.payment_mode} onChange={pf('payment_mode')} opts={['bank_transfer','cheque','cash','upi','neft','rtgs']} />
          <Inp label="Approved By" value={payForm.approved_by} onChange={pf('approved_by')} placeholder="Approver name" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={pay} style={{ flex:1, justifyContent:'center' }}>Confirm Payment</Btn>
            <Btn variant="ghost" onClick={()=>setPayModal(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── LABOUR ────────────────────────────────────────────────────────────────
function LabourTab({ projects }) {
  const [labour, setLabour] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ project_id:'', contractor_id:'', attendance_date:today(), worker_category:'mason', workers_present:'', workers_absent:'0', overtime_hours:'0', daily_wage:'', work_done_description:'', recorded_by:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = useCallback(() => {
    const q = filterProject ? `?project_id=${filterProject}` : '';
    fetch(`${API}/labour${q}`).then(r=>r.json()).then(d=>setLabour(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/contractors`).then(r=>r.json()).then(d=>setContractors(Array.isArray(d)?d:[])).catch(()=>{});
  }, [filterProject]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    await fetch(`${API}/labour`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const todayWorkers = labour.filter(l=>l.attendance_date?.slice(0,10)===today()).reduce((s,l)=>s+parseInt(l.workers_present||0),0);
  const todayWage = labour.filter(l=>l.attendance_date?.slice(0,10)===today()).reduce((s,l)=>s+parseFloat(l.total_wage||0),0);

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Labour Attendance</div>
        <div style={{ display:'flex', gap:10 }}>
          <select value={filterProject} onChange={e=>setFilterProject(e.target.value)}
            style={{ padding:'7px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
            <option value="">All Projects</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Btn onClick={()=>setShowModal(true)}>+ Log Attendance</Btn>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:18 }}>
        <Stat label="Workers Today" value={todayWorkers} sub="All projects" />
        <Stat label="Wages Today" value={inr(todayWage)} sub="All workers" color={C.amber} bg={C.amberBg} />
        <Stat label="Total Records" value={labour.length} sub="Attendance logs" color={C.blue} bg={C.blueBg} />
      </div>

      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><Th c="Date" /><Th c="Project" /><Th c="Contractor" /><Th c="Category" /><Th c="Present" /><Th c="Absent" /><Th c="OT Hours" /><Th c="Daily Wage" /><Th c="Total" /><Th c="Work Done" /></tr></thead>
            <tbody>
              {labour.length===0 ? <tr><td colSpan={10}><Empty msg="No attendance logged yet" /></td></tr>
                : labour.map(l => (
                  <tr key={l.id}>
                    <Td style={{ fontSize:12, color:C.text3 }}>{l.attendance_date?.slice(0,10)}</Td>
                    <Td style={{ color:C.text3, fontSize:12 }}>{l.project_name}</Td>
                    <Td style={{ color:C.text3 }}>{l.contractor_name||'Direct'}</Td>
                    <Td><span style={{ fontSize:12, background:C.bg, padding:'2px 8px', borderRadius:6 }}>{l.worker_category}</span></Td>
                    <Td><span style={{ fontWeight:700, color:C.accent }}>{l.workers_present}</span></Td>
                    <Td style={{ color:l.workers_absent>0?C.red:C.text3 }}>{l.workers_absent}</Td>
                    <Td style={{ color:C.text3 }}>{l.overtime_hours}h</Td>
                    <Td style={{ color:C.text3 }}>{inr(l.daily_wage)}</Td>
                    <Td><span style={{ fontWeight:700 }}>{inr(l.total_wage)}</span></Td>
                    <Td style={{ fontSize:12, color:C.text3, maxWidth:200 }}>{l.work_done_description?.slice(0,60)}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && (
        <Modal title="Log Labour Attendance" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Project" value={form.project_id} onChange={f('project_id')} opts={[{value:'',label:'Select project'},...projects.map(p=>({value:p.id,label:p.name}))]} />
            <Inp label="Contractor" value={form.contractor_id} onChange={f('contractor_id')} opts={[{value:'',label:'Direct Labour'},...contractors.map(c=>({value:c.id,label:c.name}))]} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Date" value={form.attendance_date} onChange={f('attendance_date')} type="date" />
            <Inp label="Worker Category" value={form.worker_category} onChange={f('worker_category')} opts={['mason','helper','carpenter','electrician','plumber','painter','tile fixer','steel fixer','supervisor','other']} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <Inp label="Present" value={form.workers_present} onChange={f('workers_present')} type="number" />
            <Inp label="Absent" value={form.workers_absent} onChange={f('workers_absent')} type="number" />
            <Inp label="OT Hours" value={form.overtime_hours} onChange={f('overtime_hours')} type="number" />
          </div>
          <Inp label="Daily Wage per Worker (Rs)" value={form.daily_wage} onChange={f('daily_wage')} type="number" placeholder="e.g. 600" />
          <Inp label="Work Done Today" value={form.work_done_description} onChange={f('work_done_description')} placeholder="e.g. Brick masonry on north wall, 2nd floor" />
          <Inp label="Recorded By" value={form.recorded_by} onChange={f('recorded_by')} placeholder="Supervisor name" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Save</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── SNAGS ─────────────────────────────────────────────────────────────────
function SnagsTab({ projects }) {
  const [snags, setSnags] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ project_id:'', description:'', category:'workmanship', severity:'medium', assigned_to:'', target_date:'' });
  const f = k => v => setForm(p=>({...p,[k]:v}));

  const load = () => { fetch(`${API}/snags`).then(r=>r.json()).then(d=>setSnags(Array.isArray(d)?d:[])).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await fetch(`${API}/snags`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
    setShowModal(false); load();
  };

  const resolve = async (id) => {
    const notes = window.prompt('Resolution notes?') || 'Resolved';
    await fetch(`${API}/snags/${id}/resolve`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ notes }) });
    load();
  };

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Snag List - Quality Issues</div>
        <Btn onClick={()=>setShowModal(true)}>+ Report Snag</Btn>
      </div>
      {snags.length===0
        ? <Card style={{ padding:40, textAlign:'center', color:C.accent, fontWeight:600 }}>No open snags - excellent quality control!</Card>
        : snags.map(s => {
          const isOverdue = s.target_date && s.target_date < today();
          return (
            <Card key={s.id} style={{ padding:20, marginBottom:12, borderLeft:`4px solid ${statusColor(s.severity)[0]}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                    <Badge label={s.severity} />
                    <span style={{ fontSize:12, background:C.bg, padding:'2px 8px', borderRadius:6, color:C.text3 }}>{s.category}</span>
                    <span style={{ fontSize:12, color:C.text3 }}>{s.project_name}</span>
                  </div>
                  <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:4 }}>{s.description}</div>
                  <div style={{ fontSize:12, color:C.text3 }}>
                    Reported: {s.reported_date?.slice(0,10)} | Assigned: {s.assigned_to||'Unassigned'} |
                    Target: <span style={{ color:isOverdue?C.red:C.text3, fontWeight:isOverdue?700:400 }}>{s.target_date?.slice(0,10)||'TBD'}{isOverdue?' OVERDUE':''}</span>
                  </div>
                </div>
                <Btn variant="light" onClick={()=>resolve(s.id)} style={{ padding:'5px 14px', fontSize:12 }}>Resolve</Btn>
              </div>
            </Card>
          );
        })}
      {showModal && (
        <Modal title="Report Snag" onClose={()=>setShowModal(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Project" value={form.project_id} onChange={f('project_id')} opts={[{value:'',label:'Select project'},...projects.map(p=>({value:p.id,label:p.name}))]} />
            <Inp label="Category" value={form.category} onChange={f('category')} opts={['workmanship','material','design','safety','finishing','structural','plumbing','electrical','other']} />
          </div>
          <Inp label="Description" value={form.description} onChange={f('description')} placeholder="Describe the quality issue in detail..." />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="Severity" value={form.severity} onChange={f('severity')} opts={['low','medium','high']} />
            <Inp label="Assigned To" value={form.assigned_to} onChange={f('assigned_to')} placeholder="Contractor/staff" />
          </div>
          <Inp label="Target Resolution Date" value={form.target_date} onChange={f('target_date')} type="date" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save} style={{ flex:1, justifyContent:'center' }}>Report</Btn>
            <Btn variant="ghost" onClick={()=>setShowModal(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── ANALYTICS ─────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { fetch(`${API}/analytics`).then(r=>r.json()).then(setData).catch(()=>{}); }, []);
  if (!data) return <div style={{ padding:40, textAlign:'center', color:C.text3 }}>Loading analytics...</div>;

  return (
    <div style={{ padding:'24px 28px' }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:20 }}>Construction Analytics</div>

      <Card style={{ padding:20, marginBottom:16 }}>
        <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Budget vs Spent per Project</div>
        {(data.budget_vs_spent||[]).map(p => {
          const budget = parseFloat(p.total_budget||0);
          const spent = parseFloat(p.spent_to_date||0);
          const pct = budget > 0 ? Math.min((spent/budget)*100, 100) : 0;
          const overrun = spent > budget;
          return (
            <div key={p.name} style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{p.name} - {p.progress_pct}% done</span>
                <span style={{ fontSize:13, fontWeight:700, color:overrun?C.red:C.text }}>{inr(spent)} / {inr(budget)}</span>
              </div>
              <div style={{ height:8, background:C.border, borderRadius:99 }}>
                <div style={{ height:8, width:`${pct}%`, background:overrun?C.red:pct>80?C.amber:C.accent, borderRadius:99 }} />
              </div>
              {overrun && <div style={{ fontSize:11, color:C.red, fontWeight:700, marginTop:4 }}>OVER BUDGET by {inr(spent-budget)}</div>}
            </div>
          );
        })}
      </Card>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Top Material Usage</div>
          {(data.material_usage||[]).length===0 ? <Empty msg="No usage data yet" />
            : (data.material_usage||[]).map((m,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{m.name}</div>
                  <div style={{ fontSize:11, color:C.text3 }}>{parseFloat(m.total_issued||0).toFixed(1)} {m.unit} issued</div>
                </div>
                <div style={{ fontSize:12, fontWeight:700, color:parseFloat(m.avg_wastage||0)>15?C.red:C.accent }}>
                  {parseFloat(m.avg_wastage||0).toFixed(1)}% wastage
                </div>
              </div>
            ))}
        </Card>

        <Card style={{ padding:20 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>Pilferage Analysis</div>
          {(data.pilferage_summary||[]).length===0
            ? <div style={{ padding:'30px 20px', textAlign:'center', color:C.accent, fontWeight:600 }}>No pilferage incidents recorded</div>
            : (data.pilferage_summary||[]).map((p,i) => (
              <div key={i} style={{ padding:'10px 12px', background:C.redBg, borderRadius:8, marginBottom:8, border:`1px solid ${C.redBorder}` }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.red }}>{p.name}</div>
                <div style={{ fontSize:12, color:C.text3 }}>{p.incidents} incidents | Avg wastage: {parseFloat(p.avg_wastage||0).toFixed(1)}%</div>
              </div>
            ))}
        </Card>
      </div>

      <Card style={{ padding:20, background:'linear-gradient(135deg,#0A1A0F,#0F2A14)', border:'none' }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.accent, marginBottom:14 }}>AI Construction Recommendations</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
          {[
            ['Cement Control', 'Standard: 6.4 bags per cum of concrete. Flag issuances deviating more than 10%.'],
            ['Steel Tracking', 'Weigh TMT bars before issuing. Theoretical weight per metre: 8mm=0.395kg, 12mm=0.888kg.'],
            ['Labour Output', 'Mason: 400-500 bricks/day. Plasterer: 10-12 sqm/day. Flag consistent underperformance.'],
            ['Retention Money', 'Hold 10% retention until 12-month defect liability period completion.'],
            ['Material Prediction', 'Issue only 105% of theoretical BOQ requirement. Track returns daily.'],
            ['Contractor Rating', 'Rate on quality, timeline adherence, and material efficiency after each project.'],
          ].map(([title, text]) => (
            <div key={title} style={{ padding:'14px 16px', background:'rgba(58,175,88,.08)', borderRadius:10, border:'1px solid rgba(58,175,88,.2)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:6 }}>{title}</div>
              <div style={{ fontSize:12, color:'rgba(240,248,242,.7)', lineHeight:1.5 }}>{text}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function Construction() {
  const [tab, setTab] = useState('dashboard');
  const [projects, setProjects] = useState([]);

  const loadProjects = useCallback(() => {
    fetch(`${API}/projects`).then(r=>r.json()).then(d=>setProjects(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  const tabs = [
    ['dashboard','Dashboard'],['projects','Projects'],['milestones','Milestones'],
    ['materials','Materials'],['contractors','Contractors'],['bills','Bills'],
    ['labour','Labour'],['snags','Snag List'],['analytics','Analytics'],
  ];

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'0 28px', position:'sticky', top:0, zIndex:50 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', height:60 }}>
          <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Construction Management</div>
          <div style={{ fontSize:12, color:C.text3 }}>{projects.length} projects</div>
        </div>
        <div style={{ display:'flex', gap:2, overflowX:'auto' }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={()=>setTab(id)}
              style={{ padding:'10px 16px', border:'none', background:'transparent',
                color: tab===id?C.accent:C.text3, fontSize:13, fontWeight:tab===id?700:500,
                cursor:'pointer', whiteSpace:'nowrap',
                borderBottom: tab===id?`2px solid ${C.accent}`:'2px solid transparent' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab==='dashboard'   && <Dashboard projects={projects} setTab={setTab} />}
      {tab==='projects'    && <ProjectsTab projects={projects} reload={loadProjects} />}
      {tab==='milestones'  && <MilestonesTab projects={projects} />}
      {tab==='materials'   && <MaterialsTab projects={projects} />}
      {tab==='contractors' && <ContractorsTab />}
      {tab==='bills'       && <BillsTab projects={projects} />}
      {tab==='labour'      && <LabourTab projects={projects} />}
      {tab==='snags'       && <SnagsTab projects={projects} />}
      {tab==='analytics'   && <AnalyticsTab />}
    </div>
  );
}
